/**
 * MT-01 Machine translation (INV-CH-MT-01, ENGINE-SPEC §4; change C-MT01-CAP). The translation
 * axis (excluded from the authorship composite, as the seed's `flag`).
 *
 * Eight weighted sub-signals per 1,000 prose words:
 *   subject-dropped starts ×2 · run-on capital joins ×1.2 · floating quotes ×1 · "X and X"
 *   duplications ×2 · mixed-script residue ×2 · unbalanced quote sets ×1.5 · space before
 *   punctuation ×1.2 · leading-space lines ×0.8.
 *
 * `bilingual` (mixed-script residue) and `parity` (an odd count of ' or ") are document-level
 * observations in the seed; each is one weighted event here, localised to the paragraph that holds
 * most of the non-Latin characters / the last quote mark of that kind.
 *
 * C-MT01-CAP: the seed capped the score at 45 below three raw events. The cap now applies unless
 * events ≥ 3 AND at least 0.2 events per 1,000 words, so three isolated accidents in a very long
 * document cannot lift the channel (translation leaves repeated fingerprints, not single accidents).
 */
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { type CompiledEnglish, compileEnglishLexicons } from '../lexicons/index.ts';
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { fmt, proseParagraphs } from './helpers.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	Measurement,
	Ramp,
	RunContext,
} from './types.ts';

/** The seed ramp with the D2 zero guard (0, 8) prepended (MT-01 zero value 8). */
const RAMP: Ramp = [
	[0, 8],
	[1, 25],
	[3, 45],
	[6, 62],
	[10, 78],
	[18, 90],
	[30, 96],
];

const CAP = 45;
const CAP_MIN_EVENTS = 3;
const CAP_MIN_PER_K = 0.2;
const NEAR_DUP_EXCLUDE = new Set([
	'more',
	'less',
	'over',
	'again',
	'time',
	'round',
	'side',
	'half',
]);

export const W = {
	sdrop: 2,
	runon: 1.2,
	floatq: 1,
	neardup: 2,
	bilingual: 2,
	parity: 1.5,
	spacePunct: 1.2,
	leadSpace: 0.8,
} as const;

const F_SDROP = 1;
const F_RUNON = 2;
const F_FLOATQ = 3;
const F_NEARDUP = 4;
const F_BILINGUAL = 5;
const F_PARITY = 6;
const F_SPACEPUNCT = 7;
const F_LEADSPACE = 8;

export const mt01Def: ChannelDef = {
	id: 'MT-01',
	name: 'Machine translation',
	family: 'TRANSLATION',
	axis: 'translation',
	registers: ['prose'],
	direction: 1,
	// Flag axis (seed CH_META flag: true): outside the composite loop. One-way for the D2 zero guard.
	oneWay: true,
	estimator: 'rate',
	features: [
		{ id: 'mt', label: 'Weighted MT artefact units per 1,000 words' },
		{ id: 'subjectDrop', label: 'Subject-dropped sentence starts (×2)' },
		{ id: 'runOnCapital', label: 'Run-on capital joins (×1.2)' },
		{ id: 'floatingQuote', label: 'Floating quotes (×1)' },
		{ id: 'nearDuplicate', label: '"X and X" duplications (×2)' },
		{ id: 'bilingual', label: 'Mixed-script residue (×2)' },
		{ id: 'quoteParity', label: 'Unbalanced quote sets (×1.5)' },
		{ id: 'spaceBeforePunct', label: 'Space before punctuation (×1.2)' },
		{ id: 'leadingSpaceLine', label: 'Leading-space lines (×0.8)' },
	],
	minExposure: 350,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1000,
	unitLabel: 'per 1k words (weighted)',
	prior: derivePrior(RAMP, 1000, 'words', 1.5),
	weight: 1,
	nd: 1,
	changes: ['C-MT01-CAP'],
};

let lexCache: CompiledEnglish | undefined;
const lex = (): CompiledEnglish => {
	lexCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return lexCache;
};

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const nr = lex().channels['ch-mt01'];
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const ev = makeEvidence();
	const cnt = {
		sdrop: 0,
		runon: 0,
		floatq: 0,
		neardup: 0,
		spacePunct: 0,
		leadSpace: 0,
	};
	let otherScript = 0;
	let singles = 0;
	let doubles = 0;
	let lastSingle = -1;
	let lastSinglePara = -1;
	let lastDouble = -1;
	let lastDoublePara = -1;
	const otherPerPara = new Map<number, number>();
	// bench measurement (BENCH-V2-SPEC §5.1): the two document-level signals are carried by per-paragraph counts
	const nOther = new Float64Array(P);
	const nSingle = new Float64Array(P);
	const nDouble = new Float64Array(P);
	const kDocLevel = new Float64Array(P);
	const nEvents = new Float64Array(P);
	const otherRuns: Array<[number, number, number]> = []; // start, end, paragraph

	for (const p of proseParagraphs(doc)) {
		e[p] = doc.paragraphs.ownWords[p] as number;
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		const text = doc.text.slice(b.charStart, b.charEnd);
		const base = b.charStart;
		let kp = 0;
		const scan = (
			name: string,
			f: (m: RegExpExecArray) => [number, number] | null,
			feature: number,
			weight: number,
			counter: keyof typeof cnt
		): void => {
			const re = nr.get(name);
			re.lastIndex = 0;
			for (let m = re.exec(text); m; m = re.exec(text)) {
				const r = f(m);
				if (r) {
					ev.add(base + r[0], base + r[1], feature, weight);
					kp += weight;
					cnt[counter]++;
					nEvents[p] = (nEvents[p] as number) + 1;
				}
				if (m[0].length === 0) re.lastIndex++;
			}
			re.lastIndex = 0;
		};
		scan(
			'subjectDropStart',
			(m) => {
				const verb = (/[A-Za-z]+$/.exec(m[0]) as RegExpExecArray)[0];
				return [m.index + m[0].length - verb.length, m.index + m[0].length];
			},
			F_SDROP,
			W.sdrop,
			'sdrop'
		);
		scan('subjectDropThen', (m) => [m.index, m.index + m[0].length], F_SDROP, W.sdrop, 'sdrop');
		scan(
			'subjectDropComma',
			(m) => [m.index, m.index + m[0].trimEnd().length],
			F_SDROP,
			W.sdrop,
			'sdrop'
		);
		scan(
			'runOnCapital',
			(m) => [m.index + m[0].search(/[A-Z]/), m.index + m[0].length],
			F_RUNON,
			W.runon,
			'runon'
		);
		scan('floatingQuote', (m) => [m.index + 1, m.index + 2], F_FLOATQ, W.floatq, 'floatq');
		scan('floatingQuoteEnd', (m) => [m.index + 1, m.index + 2], F_FLOATQ, W.floatq, 'floatq');
		scan(
			'nearDuplicate',
			(m) =>
				NEAR_DUP_EXCLUDE.has((m[1] as string).toLowerCase())
					? null
					: [m.index, m.index + m[0].length],
			F_NEARDUP,
			W.neardup,
			'neardup'
		);
		scan(
			'spaceBeforePunct',
			(m) => [m.index + 1, m.index + m[0].trimEnd().length],
			F_SPACEPUNCT,
			W.spacePunct,
			'spacePunct'
		);
		scan(
			'leadingSpaceLine',
			(m) => [m.index + 1, m.index + 2],
			F_LEADSPACE,
			W.leadSpace,
			'leadSpace'
		);
		k[p] = kp;

		// mixed-script characters (runs), quote parity
		const reOther = nr.get('otherScript');
		reOther.lastIndex = 0;
		let n = 0;
		for (let m = reOther.exec(text); m; m = reOther.exec(text)) {
			n++;
			const s = base + m.index;
			const last = otherRuns[otherRuns.length - 1];
			if (last && last[2] === p && last[1] === s) last[1] = s + m[0].length;
			else otherRuns.push([s, s + m[0].length, p]);
		}
		reOther.lastIndex = 0;
		if (n > 0) {
			otherScript += n;
			otherPerPara.set(p, n);
			nOther[p] = n;
		}
		const reSingle = nr.get('singleQuote');
		reSingle.lastIndex = 0;
		for (let m = reSingle.exec(text); m; m = reSingle.exec(text)) {
			singles++;
			nSingle[p] = (nSingle[p] as number) + 1;
			lastSingle = base + m.index + m[0].length - 1;
			lastSinglePara = p;
			if (m[0].length === 0) reSingle.lastIndex++;
		}
		reSingle.lastIndex = 0;
		let at = text.indexOf('"');
		while (at >= 0) {
			doubles++;
			nDouble[p] = (nDouble[p] as number) + 1;
			lastDouble = base + at;
			lastDoublePara = p;
			at = text.indexOf('"', at + 1);
		}
	}

	// document-level signals, localised
	const wc = doc.proseWords;
	let bilingual = 0;
	const docLevel = !ctx.frozen; // with a frozen context these two are recomputed from the counts (DocState)
	if (docLevel && otherScript > 8 && wc > 80) {
		bilingual = 1;
		let best = -1;
		let bestN = -1;
		for (const [p, n] of otherPerPara) {
			if (n > bestN || (n === bestN && p < best)) {
				best = p;
				bestN = n;
			}
		}
		k[best] = (k[best] as number) + W.bilingual;
		kDocLevel[best] = (kDocLevel[best] as number) + W.bilingual;
		let first = true;
		for (const [s, en, p] of otherRuns) {
			if (p === best && first) {
				ev.add(s, en, F_BILINGUAL, W.bilingual);
				first = false;
			} else ev.add(s, en, F_BILINGUAL, 0);
		}
	}
	let parity = 0;
	if (docLevel && singles % 2 === 1 && lastSingle >= 0) {
		parity++;
		ev.add(lastSingle, lastSingle + 1, F_PARITY, W.parity);
		k[lastSinglePara] = (k[lastSinglePara] as number) + W.parity;
		kDocLevel[lastSinglePara] = (kDocLevel[lastSinglePara] as number) + W.parity;
	}
	if (docLevel && doubles % 2 === 1 && lastDouble >= 0) {
		parity++;
		ev.add(lastDouble, lastDouble + 1, F_PARITY, W.parity);
		k[lastDoublePara] = (k[lastDoublePara] as number) + W.parity;
		kDocLevel[lastDoublePara] = (kDocLevel[lastDoublePara] as number) + W.parity;
	}

	const events =
		cnt.sdrop +
		cnt.runon +
		cnt.floatq +
		cnt.neardup +
		bilingual +
		parity +
		cnt.spacePunct +
		cnt.leadSpace;
	const units =
		W.sdrop * cnt.sdrop +
		W.runon * cnt.runon +
		W.floatq * cnt.floatq +
		W.neardup * cnt.neardup +
		W.bilingual * bilingual +
		W.parity * parity +
		W.spacePunct * cnt.spacePunct +
		W.leadSpace * cnt.leadSpace;
	const density = wc > 0 ? (units / wc) * 1000 : 0;
	const summary: string[] = [];
	if (cnt.sdrop)
		summary.push(`${cnt.sdrop} subject-dropped starts: pro-drop source-language artefact`);
	if (cnt.runon) summary.push(`${cnt.runon} run-on capital joins: lost sentence boundary`);
	if (cnt.floatq)
		summary.push(`${cnt.floatq} floating quotes: spacing artefacts around quotation marks`);
	if (cnt.neardup) summary.push(`${cnt.neardup} “X and X” duplications: translation doubling`);
	if (bilingual)
		summary.push(
			`${otherScript} non-Latin characters: mixed-script residue alongside English body`
		);
	if (parity) summary.push(`${parity} unbalanced quote sets: odd quotation-mark count`);
	if (cnt.spacePunct)
		summary.push(
			`${cnt.spacePunct} space-before-punctuation: “for me : something” spacing artefact`
		);
	if (cnt.leadSpace)
		summary.push(`${cnt.leadSpace} leading-space lines: copy/pipeline indentation residue`);
	summary.push(`${fmt(units, 1)} MT units · ${fmt(density, 1)}/1000w`);
	return {
		k,
		e,
		evidence: ev,
		aux: { nOther, nSingle, nDouble, kDocLevel, nEvents },
		features: {
			units,
			events,
			subjectDrop: cnt.sdrop,
			runOnCapital: cnt.runon,
			floatingQuote: cnt.floatq,
			nearDuplicate: cnt.neardup,
			bilingual,
			quoteParity: parity,
			spaceBeforePunct: cnt.spacePunct,
			leadingSpaceLine: cnt.leadSpace,
		},
		summary,
	};
}

/**
 * C-MT01-CAP (pure): the seed capped the score at 45 below three raw events; now the cap applies
 * unless events ≥ 3 AND at least 0.2 events per 1,000 words of exposure `E`.
 */
export function mtCap(score: number, events: number, E: number): number {
	const perK = E > 0 ? (events / E) * 1000 : 0;
	return events >= CAP_MIN_EVENTS && perK >= CAP_MIN_PER_K ? score : Math.min(CAP, score);
}

const base: ChannelImpl = rateChannel(mt01Def, measure);

export const mt01: ChannelImpl = {
	def: base.def,
	phase: base.phase,
	measure: base.measure,
	run(ctx: RunContext): ChannelResult {
		const r = base.run(ctx);
		if (r.status !== 'scored') return r;
		const events = r.features.events ?? 0;
		const capped = mtCap(r.doc.score, events, r.doc.E);
		if (capped < r.doc.score) {
			r.doc.score = capped;
			r.notes.push('score capped at 45: fewer than 3 events or under 0.2 per 1,000 words');
		}
		r.doc.scoreCi90 = [
			mtCap(r.doc.scoreCi90[0], events, r.doc.E),
			mtCap(r.doc.scoreCi90[1], events, r.doc.E),
		];
		return r;
	},
};
export { mt01Def as def, mt01 as impl };
